import { supabase } from "@/integrations/supabase/client";
import { typedSupabase } from "@/integrations/supabase/database-extensions";
import { measureAsync } from "@/utils/performance";
import { reportProductsFetchError } from "@/utils/errorReporting";

export interface MembershipTier {
  id: string; // 'once' | 'twice' | 'four'
  frequency_per_year: number;
  stripe_product_id?: string | null;
  annual_amount_chf?: number | null; // unit_amount in CHF cents
  sort_order: number;
}

export interface ProductsResponse {
  products: Array<{
    id: string;
    name: string;
    product_type?: string;
    sort_order?: number;
    default_price?: { unit_amount: number };
    translations?: Record<string, unknown>;
    [key: string]: unknown;
  }>;
  categories: Array<{
    id: string;
    name: string;
    [key: string]: unknown;
  }>;
  membershipTiers?: MembershipTier[];
  timestamp: string;
  mode: string;
}

/**
 * Try to read products from the cache table directly
 * Returns null if cache miss or expired
 */
async function readFromCache(
  cacheKey: string,
): Promise<ProductsResponse | null> {
  try {
    const { data, error } = (await typedSupabase
      .from("cache")
      .select("json_payload, expires_on")
      .eq("key", cacheKey)
      .single()) as {
      data: { json_payload: ProductsResponse; expires_on: string } | null;
      error: unknown;
    };

    if (error || !data) {
      console.log(`[Cache] Miss for key: ${cacheKey}`);
      return null;
    }

    const expiresOn = new Date(data.expires_on);
    if (expiresOn < new Date()) {
      console.log(`[Cache] Expired for key: ${cacheKey}`);
      return null;
    }

    console.log(`[Cache] Hit for key: ${cacheKey}`);
    return data.json_payload as ProductsResponse;
  } catch (error) {
    console.error(`[Cache] Error reading cache:`, error);
    return null;
  }
}

/**
 * Creates a query function for fetching products
 * This is the single source of truth for product fetching logic
 * Used by both useProducts hook and App.tsx prefetcher
 *
 * Strategy: Read from cache table first (fast), fallback to edge function on miss
 */
export const createProductsQueryFn = (
  language: string,
  isDevelopment: boolean,
  market = "ch",
) => {
  return async (): Promise<ProductsResponse> => {
    const mode = isDevelopment ? "dev" : "prod";
    // Mirror the edge function's cache-key scheme: the default market keeps the
    // legacy key so existing warm caches stay valid; other markets are scoped.
    const cacheKey =
      market === "ch"
        ? `products:${language}:${mode}`
        : `products:${language}:${market}:${mode}`;

    console.log(
      `Fetching products with language: ${language}, market: ${market}, mode: ${isDevelopment ? "development" : "production"}`,
    );

    // Try cache first (direct DB read, no edge function cold start)
    const cached = await readFromCache(cacheKey);
    if (cached) {
      console.log("[Cache] Using cached products data");
      return cached;
    }

    // Cache miss - fall back to edge function
    console.log("[Cache] Miss, calling edge function");
    const response = await measureAsync(
      "fetch_products",
      () =>
        supabase.functions.invoke<ProductsResponse>("get-products", {
          body: {
            language,
            isDevelopment,
            market,
          },
        }),
      {
        language,
        market,
        mode: isDevelopment ? "development" : "production",
      },
    );

    if (response.error) {
      console.error("Error fetching products:", response.error);
      throw response.error;
    }

    const data = response.data;
    console.log("Received data from get-products:", data);
    console.log("Product count:", data?.products?.length || 0);
    console.log("Category count:", data?.categories?.length || 0);

    return data;
  };
};
